import { AttachInternals, Component, Element, h, Host, Prop, State } from '@stencil/core'

interface BreadcrumbItem {
  label: string
  url?: string
  target?: string
  home?: boolean
  active?: boolean
}
@Component({
  tag: 'br-breadcrumb',
  styleUrl: 'breadcrumb.scss',
  shadow: true,
})
/**
 * Componente de navegação do tipo breadcrumb para indicar o caminho de páginas.
 *
 * @example
 * ```html
 * <br-breadcrumb crumbs='[{"label":"Home","url":"/"},{"label":"Página Atual","active":true}]'></br-breadcrumb>
 * ```
 */
export class Breadcrumb {
  @Element() el: HTMLBrBreadcrumbElement
  @AttachInternals() elementInternals: ElementInternals

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = `br-breadcrumb-${breadcrumbId++}`

  /**
   * URL para home.
   * Caso não seja fornecido, o valor padrão será /.
   */
  @Prop() homeUrl: string = '/'

  /**
   * Define o array de objetos que receberá os nomes e links do breadcrumb.
   * Define valor padrão do breadcrumb 'defaultCrumbs'.
   */
  @Prop() crumbs: string | BreadcrumbItem[]

  private defaultCrumbs: BreadcrumbItem[] = [
    { label: 'Página 01', url: '/pagina-01' },
    { label: 'Página 02', url: '/pagina-02' },
    { label: 'Página Atual', active: true },
  ]

  /**
   * Define o estado oculto ou visível do tooltip.
   */
  @State() showTooltip: boolean = false

  /**
   * Define o posicionamento do tooltip na tela.
   */
  @State() tooltipPosition: { x: number; y: number } = { x: 0, y: 0 }

  /**
   * Define o texto do tooltip.
   */
  @State() tooltipText: string = ''

  /**
   * Define o estado isSmallScreen, usado para verificar a largura do container pai e aplicar as mudanças do formato mobile.
   * No formato mobile o breadcrumb apresenta um comportamento específico.
   */
  @State() isSmallScreen: boolean = false

  /**
   * Define se o breadcrumb deve truncar os itens por falta de espaço.
   */
  @State() isTruncate: boolean = false

  /**
   * Largura interna dos itens do breadcrumb.
   */
  @State() larguraInterna: number = 0

  /**
   * Largura do container pai do breadcrumb.
   */
  @State() larguraPai: number = 0

  /**
   * Define o estado do botão 'Folder'.
   * Caso a lista dropdown esteja oculta o ícone no botão será 'folder-plus'.
   * Caso a lista dropdown esteja visível o ícone no botão será 'folder-minus'.
   */
  @State() isFolderOpen: boolean = false

  /**
   * Verifica se o texto do link contém mais de 20 caracteres e realiza o truncamento deste caso positivo.
   * O número de caracteres pode ser configurado na variável 'charactersNumberTruncate'.
   */
  private trunc(word: string) {
    return word.length > charactersNumberTruncate ? `${word.slice(0, charactersNumberTruncate)}...` : word
  }

  /**
   * Exibe o tooltip e define seu posicionamento, caso o usuário realize o 'mouse over'.
   */
  private handleMouseOver(event: MouseEvent, text: string) {
    const target = event.target as HTMLElement | null
    if (!target) return

    const rect = target.getBoundingClientRect()
    this.tooltipPosition = { x: rect.left + rect.width / 2, y: rect.top - 10 }

    if (text.length > charactersNumberTruncate) {
      this.tooltipText = text
      this.showTooltip = true
    }
  }

  /**
   * Esconde o tooltip caso o usuário realize o 'mouse out'.
   */
  private handleMouseOut() {
    this.showTooltip = false
  }

  /**
   * Converte de string para JSON o valor passado na propriedade 'crumbs'.
   * Retorna o valor padrão do Breadcrumbs caso crumbs seja vazio ou inválido
   */
  private getParsedItems(): BreadcrumbItem[] {
    if (!this.crumbs || this.crumbs === '') {
      return this.defaultCrumbs
    }

    try {
      const parsed = typeof this.crumbs === 'string' ? JSON.parse(this.crumbs) : this.crumbs
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : this.defaultCrumbs
    } catch {
      console.error('Invalid JSON format for crumbs:', this.crumbs)
      return this.defaultCrumbs
    }
  }

  /**
   * Ao clicar no botão HOME, direciona o usuário para URL passada na propriedade 'homeUrl'.
   */
  private handleHomeClick() {
    window.location.href = this.homeUrl
  }

  /**
   * Altera o estado do botão 'Folder', caso a lista dropdown esteja aberta ou fechada.
   */
  private toggleFolder() {
    this.isFolderOpen = !this.isFolderOpen
  }

  /**
   * Define as classes CSS usadas no breadcrumb.
   */
  private getCssClassMap(): { [key: string]: boolean } {
    return {
      'br-breadcrumb': true,
      small: this.isSmallScreen,
    }
  }

  /**
   * Atualiza o estado 'isSmallScreen' com base no tamanho do container pai, se menor que 570px.
   */
  private resizeObserver: ResizeObserver

  /**
   * Atualiza os estados de largura do breadcrumb ao detectar mudanças no container pai.
   */
  private updateContainerSize = (entries: ResizeObserverEntry[]) => {
    for (const entry of entries) {
      const containerWidth = entry.contentRect.width
      this.isSmallScreen = containerWidth < 570
      this.larguraPai = containerWidth
      this.calcularLarguraItens()
    }
  }

  /**
   * Adiciona o 'resizeObserver' para checar o tamanho do container pai.
   */
  connectedCallback() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.updateContainerSize)
      if (this.el.parentElement !== null) {
        this.resizeObserver.observe(this.el.parentElement)
      }
    }
  }

  /**
   * Remove o 'resizeObserver'.
   */
  disconnectedCallback() {
    if (this.resizeObserver != null) {
      this.resizeObserver.disconnect()
    }
  }

  /**
   * Calcula a largura total dos itens do breadcrumb para determinar se precisa de truncamento.
   */
  private calcularLarguraItens() {
    requestAnimationFrame(() => {
      const itens = this.el.shadowRoot?.querySelectorAll('.crumb, .mobile-active-page, br-item')
      if (itens) {
        let totalWidth = 0
        itens.forEach((item) => {
          totalWidth += (item as HTMLElement).getBoundingClientRect().width
        })
        this.larguraInterna = totalWidth
        this.isTruncate = this.larguraInterna > this.larguraPai
      }
    })
  }

  /**
   * Executa cálculos de largura após o componente ser carregado.
   */
  componentDidLoad() {
    this.calcularLarguraItens()
  }

  /**
   * Renderiza botão Home
   */
  private renderHomeButton() {
    const buttonFolderClass = this.isFolderOpen ? 'fa6-solid:folder-minus' : 'fa6-solid:folder-plus'
    return (
      <br-button
        id="circle"
        shape="circle"
        aria-label={this.isSmallScreen ? 'Exibir lista de páginas' : 'Voltar para Home'}
        onClick={this.isSmallScreen ? () => this.toggleFolder() : () => this.handleHomeClick()}
      >
        <br-icon icon-name={`${this.isSmallScreen ? buttonFolderClass : 'fa6-solid:house'}`}></br-icon>
      </br-button>
    )
  }

  /**
   * Renderiza botão Folder no comportamento truncado
   */
  private renderTruncateButton() {
    if (this.isTruncate && !this.isSmallScreen) {
      const buttonFolderClass = this.isFolderOpen ? 'fa6-solid:folder-minus' : 'fa6-solid:folder-plus'
      return (
        <div class="buttonFolderGroup">
          <br-icon class="br-icon" icon-name="fa6-solid:chevron-right" width="12" height="12"></br-icon>
          <br-button
            id="circle"
            shape="circle"
            aria-label={this.isFolderOpen ? 'Recolher lista de páginas' : 'Exibir lista de páginas'}
            onClick={() => this.toggleFolder()}
            onKeyDown={(event) => this.handleKeyDown(event)}
          >
            <br-icon icon-name={buttonFolderClass}></br-icon>
          </br-button>
        </div>
      )
    }
    return null
  }

  /**
   * Renderiza itens do Breadcrumb no comportamento mobile ou truncado
   */
  private renderBreadcrumbItems() {
    const parsedItems = this.getParsedItems()
    const lastItem = parsedItems[parsedItems.length - 2]

    return this.isSmallScreen || this.isTruncate ? (
      <div class="buttonFolderGroup">
        {this.isTruncate && !this.isSmallScreen && lastItem ? (
          <span>
            <br-icon class="br-icon" icon-name="fa6-solid:chevron-right" width="12" height="12"></br-icon>
            <a
              id="nextCrumb"
              href={lastItem.url || 'javascript:void(0)'}
              target={lastItem.target || '_self'}
              onMouseOver={(event) => this.handleMouseOver(event, lastItem.label)}
              onMouseOut={() => this.handleMouseOut()}
            >
              {this.trunc(lastItem.label)}
            </a>
          </span>
        ) : null}
        {this.renderMobileActiveItem(parsedItems)}
        {this.renderBreadcrumbList(parsedItems)}
      </div>
    ) : (
      parsedItems.map((item, index) => (
        <li
          key={index}
          class={`crumb ${item.home ? 'home' : ''}`}
          data-active={item.active ? 'active' : null}
          aria-label={`Página ${index + 1} de ${parsedItems.length}`}
        >
          {this.renderBreadcrumbLink(item)}
        </li>
      ))
    )
  }

  /**
   * Renderiza item da página atual
   */
  private renderMobileActiveItem(parsedItems: BreadcrumbItem[]) {
    const activeItem = parsedItems.find((item) => item.active)
    if (!activeItem) return null
    return (
      <span class="mobile-active-page">
        <br-icon class="br-icon" icon-name="fa6-solid:chevron-right" width="12" height="12"></br-icon>
        <span aria-current="page" data-active="active">
          {activeItem.label}
        </span>
      </span>
    )
  }

  /**
   * Renderiza Lista dropdown contendo os itens do Breadcrumb
   */
  private renderBreadcrumbList(parsedItems: BreadcrumbItem[]) {
    return (
      <br-list class={this.isFolderOpen ? 'mobile-crumb-list show' : 'mobile-crumb-list'}>
        {this.isSmallScreen && (
          <div aria-label={`Página 1 de ${parsedItems.length}`}>
            <br-item>
              <a href={this.homeUrl} target="_self">
                Página Inicial
              </a>
            </br-item>
            <span class="br-divider"></span>
          </div>
        )}
        {parsedItems.map((item, index) => {
          if (this.isTruncate && !this.isSmallScreen && index === parsedItems.length - 2) {
            return null
          }
          return (
            !item.active && (
              <div
                key={index}
                aria-label={
                  this.isTruncate && !this.isSmallScreen
                    ? `Página ${index + 1} de ${parsedItems.length - 2}`
                    : `Página ${index + 2} de ${parsedItems.length}`
                }
              >
                <br-item>
                  <a
                    id={index == 0 ? 'firstElementList' : ''}
                    href={item.url || 'javascript:void(0)'}
                    target={item.target || '_self'}
                    onMouseOver={(event) => this.handleMouseOver(event, item.label)}
                    onMouseOut={() => this.handleMouseOut()}
                    onKeyDown={
                      index == parsedItems.length - 3 && this.isFolderOpen && this.isTruncate && !this.isSmallScreen
                        ? (event) => {
                            if (event.key === 'Tab') {
                              this.handleKeyDownOut(event)
                              this.toggleFolder()
                            }
                          }
                        : null
                    }
                  >
                    {this.trunc(item.label)}
                  </a>
                </br-item>
                <span class="br-divider"></span>
              </div>
            )
          )
        })}
      </br-list>
    )
  }

  /**
   * Renderiza itens do Breadcrumb no formato desktop
   */
  private renderBreadcrumbLink(item: BreadcrumbItem) {
    return item.active ? (
      <span>
        <br-icon class="br-icon" icon-name="fa6-solid:chevron-right" width="12" height="12"></br-icon>
        <span aria-current="page">{item.label}</span>
      </span>
    ) : (
      <span>
        <br-icon class="br-icon" icon-name="fa6-solid:chevron-right" width="12" height="12"></br-icon>
        <a
          href={item.url || 'javascript:void(0)'}
          target={item.target || '_self'}
          onMouseOver={(event) => this.handleMouseOver(event, item.label)}
          onMouseOut={() => this.handleMouseOut()}
        >
          {this.trunc(item.label)}
        </a>
      </span>
    )
  }

  /**
   * Renderiza Tooltip
   */
  private renderTooltip() {
    if (!this.showTooltip) return null
    return (
      <div class="tooltip-box" style={{ left: `${this.tooltipPosition.x}px`, top: `${this.tooltipPosition.y}px` }}>
        {this.tooltipText}
      </div>
    )
  }

  /**
   * Controla navegação pelo teclado após lista de itens aberta
   * Dispara foco no primeiro item da lista flutuante
   */
  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      if (this.isFolderOpen && this.isTruncate && !this.isSmallScreen) {
        setTimeout(() => {
          const element = this.el.shadowRoot?.getElementById('firstElementList')
          if (element) {
            element.focus()
          }
        }, 0) // Aguarda um ciclo de renderização
      }
    }
  }

  /**
   * Ao finalizar navegação por teclado dentro da lista, dispara foco no item fora da lista flutuante
   */
  private handleKeyDownOut(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      if (this.isFolderOpen && this.isTruncate && !this.isSmallScreen) {
        setTimeout(() => {
          const element = this.el.shadowRoot?.getElementById('nextCrumb')
          if (element) {
            element.focus()
          }
        }, 0)
      }
    }
  }

  render() {
    return (
      <Host>
        <nav class={this.getCssClassMap()} aria-label="Breadcrumbs">
          <ol class="crumb-list">
            {this.renderHomeButton()}
            {this.renderTruncateButton()}
            {this.renderBreadcrumbItems()}
          </ol>
        </nav>
        {this.renderTooltip()}
      </Host>
    )
  }
}

/**
 * ID do Breadcrumb
 */
let breadcrumbId = 0

/**
 * Limite de caracteres para o truncamento
 */
const charactersNumberTruncate = 20
